import React, { useState, useEffect } from 'react';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser } from '../services/auth';
import { 
  Button, TextField, Typography, Container, Box, Select, MenuItem, 
  FormControl, InputLabel, Grid, Card, CardContent, Paper, Divider,
  Alert, Avatar, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Dialog, DialogTitle, DialogContent, 
  DialogActions, IconButton, Chip
} from '@mui/material';
import { 
  LocationOn, Phone, Email, SupportAgent, Send,
  Error as ErrorIcon, CheckCircle as SuccessIcon,
  Reply, Close, Visibility, Check, Build, Lock, LockOpen
} from '@mui/icons-material';
import { deepPurple, green, orange, red, blue } from '@mui/material/colors';
import { getTickets, createTicket, updateTicket } from '../services/riderService';

const Support = () => {
  const [userRole, setUserRole] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    type: '',
    message: ''
  });
  const [replyMessage, setReplyMessage] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [actionType, setActionType] = useState('view');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const user = getCurrentUser();
    if (user) {
      setUserRole(user.role);
    }
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const data = await getTickets();
      console.log('Fetched tickets:', data);
      setTickets(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load tickets');
      console.error('Error fetching tickets:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await createTicket(formData);
      setSuccess('Ticket submitted successfully!');
      setError('');
      setFormData({ name: '', email: '', subject: '', type: '', message: '' });
      setTimeout(() => navigate('/support'), 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit ticket');
    } finally {
      setLoading(false);
    }
  };

  const handleReplySubmit = async () => {
    try {
      await updateTicket(selectedTicket.id, { reply: replyMessage });
      await fetchTickets();
      setSuccess('Reply sent successfully!');
      setError('');
      setReplyMessage('');
      setOpenDialog(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send reply');
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    try {
      await updateTicket(selectedTicket.id, { 
        status: newStatus,
        reply: replyMessage || 'Ticket status updated'
      });
      await fetchTickets();
      setSuccess(`Ticket status updated to ${newStatus}!`);
      setError('');
      setReplyMessage('');
      setOpenDialog(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update status');
    }
  };

  const handleViewTicket = (ticket, action = 'view') => {
    setSelectedTicket(ticket);
    setActionType(action);
    setOpenDialog(true);
    if (action === 'reply') {
      setReplyMessage('');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return orange[500];
      case 'in-progress': return blue[500];
      case 'replied': return green[500];
      case 'closed': return deepPurple[500];
      default: return red[500];
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open': return <LockOpen />;
      case 'in-progress': return <Build />;
      case 'replied': return <Check />;
      case 'closed': return <Lock />;
      default: return <ErrorIcon />;
    }
  };

  // Normalize role and compute access once
  const normalizedRole = (userRole || '').toLowerCase();
  const canManageTickets = ['admin', 'student employee', 'student_employee'].includes(normalizedRole);

  return (
    <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
            position: 'relative' // Added for proper footer positioning
          }}>
            <Navbar/>
    <Container maxWidth="lg" sx={{ pt:10,
          mt: 4, 
          mb: 6,
          flex: 1, // Takes up all available space
          pb: 8 }}>
      {canManageTickets ? (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h4" fontWeight="bold" color={deepPurple[700]}>
              Support Tickets
            </Typography>
            <Button 
              variant="contained" 
              color="primary"
              onClick={() => navigate('/support/new')}
            >
              Create New Ticket
            </Button>
          </Box>

          {error && (
            <Alert severity="error" icon={<ErrorIcon />} sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" icon={<SuccessIcon />} sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          <TableContainer component={Paper} elevation={3}>
            <Table>
              <TableHead sx={{ backgroundColor: deepPurple[50] }}>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tickets.map((ticket, index) => (
                  <TableRow key={ticket.id || ticket._id || index}>
                    <TableCell>{ticket.id ?? ticket._id ?? '-'}</TableCell>
                    <TableCell>{ticket.subject ?? '-'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={ticket.type} 
                        size="small" 
                        sx={{ textTransform: 'capitalize' }} 
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        icon={getStatusIcon(ticket.status)}
                        label={ticket.status} 
                        size="small" 
                        sx={{ 
                          backgroundColor: getStatusColor(ticket.status),
                          color: 'white',
                          textTransform: 'capitalize'
                        }} 
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(ticket.timestamp).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <IconButton 
                        color="primary" 
                        onClick={() => handleViewTicket(ticket)}
                      >
                        <Visibility />
                      </IconButton>
                      {ticket.status !== 'closed' && (
                        <>
                          <IconButton 
                            color="secondary" 
                            onClick={() => handleViewTicket(ticket, 'reply')}
                          >
                            <Reply />
                          </IconButton>
                          <IconButton 
                            color="primary" 
                            onClick={() => handleViewTicket(ticket, 'status')}
                          >
                            <Build />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          {/* Header */}
          <Box 
            sx={{ 
              backgroundColor: deepPurple[500], 
              color: 'white', 
              p: 3,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <Avatar sx={{ bgcolor: deepPurple[300], mr: 2 }}>
              <SupportAgent />
            </Avatar>
            <Box>
              <Typography variant="h4" fontWeight="bold">Customer Support</Typography>
              <Typography variant="subtitle2">We're here to help you with any issues</Typography>
            </Box>
          </Box>

          <Grid container spacing={0}>
            {/* Left Section - Contact Info */}
            <Grid item xs={12} md={5} sx={{ bgcolor: '#f9f9f9', p: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ color: deepPurple[700], mb: 3 }}>
                Contact Information
              </Typography>
              
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LocationOn color="primary" sx={{ mr: 2, fontSize: 30 }} />
                  <Box>
                    <Typography variant="h6" fontWeight="medium">Our Location</Typography>
                    <Typography color="textSecondary">
                      506 Dolorosa St, San Antonio,<br />
                      TX 78204, USA
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Phone color="primary" sx={{ mr: 2, fontSize: 30 }} />
                  <Box>
                    <Typography variant="h6" fontWeight="medium">Phone Number</Typography>
                    <Typography color="textSecondary">(+1) 123-456-7890</Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Email color="primary" sx={{ mr: 2, fontSize: 30 }} />
                  <Box>
                    <Typography variant="h6" fontWeight="medium">Email Address</Typography>
                    <Typography color="textSecondary">scooterlab@utsa.edu</Typography>
                  </Box>
                </Box>
              </Box>

              <Typography variant="body1" paragraph sx={{ mt: 4 }}>
                Our support team is available Monday to Friday, 9am to 5pm CST.
              </Typography>
            </Grid>

            {/* Right Section - Form */}
            <Grid item xs={12} md={7} sx={{ p: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ color: deepPurple[700], mb: 3 }}>
                Submit a Support Ticket
              </Typography>

              {error && (
                <Alert severity="error" icon={<ErrorIcon />} sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}
              {success && (
                <Alert severity="success" icon={<SuccessIcon />} sx={{ mb: 3 }}>
                  {success}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Your Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Your Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      margin="normal"
                    />
                  </Grid>
                </Grid>

                <TextField
                  fullWidth
                  label="Subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  margin="normal"
                />

                <FormControl fullWidth margin="normal" variant="outlined">
                  <InputLabel>Issue Type</InputLabel>
                  <Select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    required
                    label="Issue Type"
                  >
                    <MenuItem value="technical">Technical Issue</MenuItem>
                    <MenuItem value="renewal">Renewal Request</MenuItem>
                    <MenuItem value="general">General Question</MenuItem>
                    <MenuItem value="scooter">Scooter Problem</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Your Message"
                  name="message"
                  multiline
                  rows={6}
                  value={formData.message}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  margin="normal"
                  sx={{ mt: 2 }}
                />

                <Button 
                  type="submit" 
                  variant="contained" 
                  color="primary" 
                  size="large"
                  startIcon={<Send />}
                  sx={{ mt: 3, py: 1.5, px: 4 }}
                >
                  Submit Ticket
                </Button>
              </form>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Ticket View/Reply Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: deepPurple[500], color: 'white' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {actionType === 'view' ? 'Ticket Details' : 
               actionType === 'reply' ? 'Reply to Ticket' : 'Update Ticket Status'}
            </Typography>
            <IconButton onClick={() => setOpenDialog(false)} sx={{ color: 'white' }}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                <strong>From:</strong> {selectedTicket?.name} ({selectedTicket?.email})
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Subject:</strong> {selectedTicket?.subject}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Type:</strong> {selectedTicket?.type}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Submitted:</strong> {new Date(selectedTicket?.timestamp).toLocaleString()}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Status:</strong> 
                <Chip 
                  icon={getStatusIcon(selectedTicket?.status)}
                  label={selectedTicket?.status} 
                  size="small" 
                  sx={{ 
                    ml: 1,
                    backgroundColor: getStatusColor(selectedTicket?.status),
                    color: 'white'
                  }} 
                />
              </Typography>
              {selectedTicket?.repliedAt && (
                <Typography variant="subtitle1" gutterBottom>
                  <strong>Replied:</strong> {new Date(selectedTicket?.repliedAt).toLocaleString()}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
                <Typography variant="subtitle2" gutterBottom>
                  <strong>Original Message:</strong>
                </Typography>
                <Typography paragraph>
                  {selectedTicket?.message}
                </Typography>
              </Paper>
            </Grid>
            
            {selectedTicket?.reply && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#e8f5e9' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    <strong>Your Reply:</strong>
                  </Typography>
                  <Typography paragraph>
                    {selectedTicket?.reply}
                  </Typography>
                </Paper>
              </Grid>
            )}

            {(actionType === 'reply' || actionType === 'status') && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={actionType === 'reply' ? "Your Reply" : "Status Update Notes"}
                  multiline
                  rows={4}
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  variant="outlined"
                  margin="normal"
                />
              </Grid>
            )}

            {actionType === 'status' && (
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>New Status</InputLabel>
                  <Select
                    value={selectedTicket?.status || 'open'}
                    onChange={(e) => {
                      const newStatus = e.target.value;
                      setSelectedTicket({...selectedTicket, status: newStatus});
                    }}
                    label="New Status"
                  >
                    <MenuItem value="open">Open</MenuItem>
                    <MenuItem value="in-progress">In Progress</MenuItem>
                    <MenuItem value="closed">Closed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setOpenDialog(false)}
            color="secondary"
          >
            Close
          </Button>
          {actionType === 'reply' && (
            <Button 
              onClick={handleReplySubmit}
              variant="contained"
              color="primary"
              startIcon={<Send />}
              disabled={!replyMessage}
            >
              Send Reply
            </Button>
          )}
          {actionType === 'status' && (
            <Button 
              onClick={() => handleStatusUpdate(selectedTicket.status)}
              variant="contained"
              color="primary"
              startIcon={<Check />}
            >
              Update Status
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
    <Box sx={{ 
            width: '100%',
            bgcolor: 'background.paper',
            boxShadow: '0 -2px 5px rgba(0,0,0,0.1)',
            position: 'relative', // Changed from fixed to relative
            mt: 'auto' // Pushes footer to bottom
          }}>
            <Footer />
          </Box>
        </Box>
  );
};

export default Support;